import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Input, Row, Select, Table, Tooltip } from 'antd'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import * as com from '../../../Common'
import NumericalInput, { CommaizedInput } from '../../../NumericalInput'
import {
    getApplicationLink,
    getValidatedURL,
    validatedURL,
} from '../applicationLink'
import {
    DEFAULT_QM_PRIMARY_INCOME,
    getPrimaryIncomeDocumentationTypes,
    getPrimaryIncomeOption,
    PRIMARY_INCOME_OPTIONS,
} from '../../../BlueratePersonalSite/primaryIncome'
import AffordabilityModal from './AffordabilityModal'
import eventBus from '../../../Bus'
import './MortgageRates.css'

const createEmptyRates = () => ({
    fixed30: [],
    fixed25: [],
    fixed20: [],
    fixed15: [],
    fixed10: [],
    arm56: [],
    arm76: [],
    arm106: [],
})

const TERMS_MAP = {
    fixed30: '30 Year',
    fixed25: '25 Year',
    fixed20: '20 Year',
    fixed15: '15 Year',
    fixed10: '10 Year',
    arm56: 'ARM 5/6',
    arm76: 'ARM 7/6',
    arm106: 'ARM 10/6',
}

function PointsSelector({ selectedPoint, setSelectedPoint }) {
    const points = ['All', '0', '0-1', '1-2']

    const index = useMemo(() => points.indexOf(selectedPoint), [selectedPoint])

    const highlightWidth = useMemo(() => {
        return 'calc((100% - 17.5px) / 4)'
    }, [])

    const highlightLeft = useMemo(() => {
        return `calc(${index} * (((100% - 17.5px) / 4) + 3.5px) + 3.5px)`
    }, [index])

    return (
        <div className="points-container">
            {points.map((point) => (
                <div
                    key={point}
                    className={`point-item ${
                        selectedPoint === point ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedPoint(point)}
                >
                    {point}
                </div>
            ))}
            <div
                className="highlight"
                style={{ width: highlightWidth, left: highlightLeft }}
            />
        </div>
    )
}

function MortgageRates({
    showQuickApplicationForm,
    firstName,
    customerid,
    loid,
    appLink,
    companyLink,
    helocLink,
    heloanLink,
    showHomeEquity,
    showAffordability,
    isPreviewMode,
    onRatesUpdate,
    loanOfficerEmail,
    isMobile,
    licensedStates = [],
    stateCapabilities = [],
    loName,
    onContact,
}) {
    const [loading, setLoading] = useState(false)
    const [loanLimits, setLoanLimits] = useState({})
    const [state, setState] = useState('CALIFORNIA')
    const [county, setCounty] = useState('SANTA CLARA COUNTY')
    const [fico, setFico] = useState(780)
    const [purpose, setPurpose] = useState('Purchase')
    const [propertyValue, setPropertyValue] = useState(500000)
    const [downPayment, setDownPayment] = useState(100000)
    const [monthlyIncome, setMonthlyIncome] = useState(50000)
    const [monthlyDebt, setMonthlyDebt] = useState(1000)
    const [propertyType, setPropertyType] = useState('singlefamilydetached')
    const [units, setUnits] = useState(2)
    const [occupancy, setOccupancy] = useState('principal')
    const [escrowWaiver, setEscrowWaiver] = useState(false)
    const [selectedLockDay, setSelectedLockDay] = useState(30)
    const [selectedPoint, setSelectedPoint] = useState('All')
    const [validated, setValidated] = useState(false)
    const [isGMCCJumbo, setIsGMCCJumbo] = useState(false)
    const [onUpdateRates, setOnUpdateRates] = useState(false)
    const [primaryIncome, setPrimaryIncome] = useState(
        DEFAULT_QM_PRIMARY_INCOME
    )

    const normalizedStateCapabilities = useMemo(() => {
        if (Array.isArray(stateCapabilities) && stateCapabilities.length > 0) {
            return stateCapabilities
                .filter((capability) => capability?.state)
                .map((capability) => ({
                    state: com.convertStateToAbbr(capability.state),
                    qm: capability.qm === true,
                    nonQm: capability.nonQm === true,
                }))
        }

        // Existing profiles support both categories until the LO explicitly
        // saves per-state capabilities.
        return (Array.isArray(licensedStates) ? licensedStates : [])
            .filter(Boolean)
            .map((licensedState) => ({
                state: com.convertStateToAbbr(licensedState),
                qm: true,
                nonQm: true,
            }))
    }, [licensedStates, stateCapabilities])

    const selectedIncomeOption = useMemo(
        () => getPrimaryIncomeOption(primaryIncome),
        [primaryIncome]
    )

    const pricingIncomeOption =
        selectedIncomeOption || PRIMARY_INCOME_OPTIONS[0]

    const selectedCapability = useMemo(
        () =>
            normalizedStateCapabilities.find(
                (capability) =>
                    capability.state === com.convertStateToAbbr(state)
            ),
        [normalizedStateCapabilities, state]
    )

    const hasSelectedCapability = useMemo(() => {
        if (!selectedCapability) return false
        return pricingIncomeOption.category === 'QM'
            ? selectedCapability.qm
            : selectedCapability.nonQm
    }, [selectedCapability, pricingIncomeOption])

    const [currentMtype, setCurrentMtype] = useState('fixed30')
    const [customerUrl, setCustomerUrl] = useState('https://app.zeitro.com')

    const [message, setMessage] = useState('')
    const [lenderNotSet, setLendersNotSet] = useState(false)
    const [ineligibleProducts, setIneligibleProducts] = useState({})
    const [loans, setLoans] = useState([])
    const [allRates, setAllRates] = useState(createEmptyRates)
    const [fixed30, setFixed30] = useState([])
    const [fixed25, setFixed25] = useState([])
    const [fixed20, setFixed20] = useState([])
    const [fixed15, setFixed15] = useState([])
    const [fixed10, setFixed10] = useState([])
    const [arm56, setArm56] = useState([])
    const [arm76, setArm76] = useState([])
    const [arm106, setArm106] = useState([])
    const [indexFixed30, setIndexFixed30] = useState(8)
    const [indexFixed25, setIndexFixed25] = useState(6)
    const [indexFixed20, setIndexFixed20] = useState(6)
    const [indexFixed15, setIndexFixed15] = useState(6)
    const [indexFixed10, setIndexFixed10] = useState(6)
    const [indexArm56, setIndexArm56] = useState(6)
    const [indexArm76, setIndexArm76] = useState(6)
    const [indexArm106, setIndexArm106] = useState(6)
    const [estOtherCosts, setEstOtherCosts] = useState(3195)
    const [showIneligible, setShowIneligible] = useState(false)

    const [calculatorOpen, setCalculatorOpen] = useState(false)
    const [showDisclaimer, setShowDisclaimer] = useState(false)
    const [rateTimestamp, setRateTimestamp] = useState("")

    const formRef = useRef()
    const ratesDisplayModeRef = useRef('loading')
    const lastCompletedQuoteKeyRef = useRef('')
    useEffect(() => {
        function updateRateChangeDay() {
            let now = new Date();
            const minutes = now.getMinutes();
            const roundedMinutes = minutes < 30 ? 0 : 30;
            now.setMinutes(roundedMinutes, 0, 0);

            const timeString = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });

            const dateString = now.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });

            setRateTimestamp(`${timeString}, ${dateString}`);
        }

        updateRateChangeDay();

        // Calculate ms until next half hour
        const now = new Date();
        const msToNextHalfHour =
            ((30 - (now.getMinutes() % 30)) * 60 - now.getSeconds()) * 1000 - now.getMilliseconds();

        const timeout = setTimeout(() => {
            updateRateChangeDay();
            // After first update, set interval every 30 minutes
            const interval = setInterval(updateRateChangeDay, 30 * 60 * 1000);
            return () => clearInterval(interval);
        }, msToNextHalfHour);

        return () => clearTimeout(timeout);
    }, []);

    const getLoanAmount = useCallback(() => {
        if (purpose == 'Purchase') {
            return parseInt(propertyValue) - parseInt(downPayment)
        } else {
            return parseInt(downPayment)
        }
    }, [purpose, propertyValue, downPayment])

    const getLtv = useCallback(() => {
        const ltv = parseFloat(((getLoanAmount() / parseInt(propertyValue)) * 100).toFixed(2))
        // const cltv = Math.ceil((getLoanAmount() + parseInt(second) + parseInt(helocsize)) / parseInt(propertyValue) * 100)
        // const hcltv = Math.ceil((getLoanAmount() + parseInt(second) + parseInt(heloclimit)) / parseInt(propertyValue) * 100)
        return [ltv, ltv, ltv]
    }, [propertyValue, getLoanAmount])

    const [ltvPercent, setLtvPercent] = useState(
        purpose === 'Purchase' ? 100 - getLtv()[0] : getLtv()[0]
    )

    const getProducts = useCallback(
        async (controller) => {
            if (!controller || !hasSelectedCapability) return

            const pmi = 0.51
            const zeitro_fee = [{ lender: 'default', percent: 1.0, fixed: 500 }]

            let _units = 1
            if (propertyType === 'twotofourfamily') _units = units

            setMessage('')

            const [ltv, cltv, hcltv] = getLtv()

            let _county = 'ZAVALA COUNTY'
            if (state != 'TEXAS') _county = county

            const body = {
                state: state,
                county: _county,
                purpose: purpose,
                loanAmount: getLoanAmount(),
                units: parseInt(_units),
                LTV: ltv,
                CLTV: cltv,
                HCLTV: hcltv,
                FICO: fico,
                occupancy: com.getOccupancy(occupancy),
                property: com.getPropertyType(propertyType),
                point: zeitro_fee,
                estimatedClosingCosts: parseFloat(estOtherCosts),
                pmi: pmi,
                getFiles: false,
                monthlyIncome: parseFloat(monthlyIncome),
                monthlyDebt: parseFloat(monthlyDebt),
                escrowWaiver: escrowWaiver,
                lockDay: selectedLockDay,
                isNonQM: pricingIncomeOption.category === 'Non-QM',
                nonQMDocTypes: getPrimaryIncomeDocumentationTypes(
                    pricingIncomeOption
                ),
                ...(pricingIncomeOption.documentationType ===
                'DebtServiceCoverageRatio'
                    ? { dscrMode: 'all' }
                    : {}),
            }

            setAllRates(createEmptyRates())
            setLoans([])
            setLendersNotSet(false)
            setLoading(true)
            const quoteKey = [
                com.convertStateToAbbr(state),
                primaryIncome || '',
                selectedLockDay,
            ].join(':')
            try {
                const response = await fetch(
                    '/data/getrefinance/' + customerid,
                    {
                        method: 'POST',
                        headers: {
                            Cache: 'no-cache',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(body),
                        signal: controller.signal,
                    }
                )
                if (response.status !== 200) {
                    setOnUpdateRates(false)
                    setMessage('Unable to retrieve rates right now.')
                    return
                }

                const pr = await response.json()
                setOnUpdateRates(false)

                if (pr.status_code === '12') {
                    setLendersNotSet(true)
                    return
                }

                const products = pr.result
                if (pr.ineligible_reasons != undefined) {
                    setIneligibleProducts(pr.ineligible_reasons)
                } else {
                    setIneligibleProducts({})
                }

                if (pr.message != undefined) {
                    setMessage(pr.message)
                } else {
                    setMessage('')
                }

                if (products === undefined) {
                    setShowIneligible(true)
                    setAllRates(createEmptyRates())
                    setLoans([])
                    return
                }

                let tmpFixed30 = []
                let tmpFixed25 = []
                let tmpFixed20 = []
                let tmpFixed15 = []
                let tmpFixed10 = []
                let tmpArm56 = []
                let tmpArm76 = []
                let tmpArm106 = []

                products.forEach((product) => {
                    const lender = product.Lender
                    const fb = product.FileB
                    product.ProductRatesCosts.forEach((pr) => {
                        const commonproduct = pr.product
                        const promotions = pr.promotions
                        const adjustments = pr.appliedAdjustments
                        const ausengine = pr.AUSEngine
                        pr.rateCosts.forEach((rate) => {
                            const pdkt = {
                                Lender: lender,
                                ...commonproduct,
                                Aus: ausengine,
                                ...rate,
                                promotions,
                                Adjustments: adjustments,
                                FileB: fb,
                            }

                            if (rate.lockDay !== selectedLockDay) {
                                return
                            }

                            pdkt.loanAmount = getLoanAmount()

                            const filterPdkt = () => {
                                if (
                                    customerid === 'siliconcitymortgage' &&
                                    pdkt.Mtype.includes('FHA')
                                ) {
                                    return false
                                }
                                if (
                                    // pdkt.Lender.includes('GMCC') ||
                                    pdkt.Lender.includes('FCB')
                                ) {
                                    return false
                                }
                                return true
                            }

                            if (filterPdkt()) {
                                if (pdkt.Arm != null) {
                                    if (pdkt.Arm.fixedperiod == 5) {
                                        tmpArm56.push(pdkt)
                                    } else if (pdkt.Arm.fixedperiod == 7) {
                                        tmpArm76.push(pdkt)
                                    } else if (pdkt.Arm.fixedperiod == 10) {
                                        tmpArm106.push(pdkt)
                                    }
                                } else {
                                    if (pdkt.Term == 30) {
                                        tmpFixed30.push(pdkt)
                                    } else if (pdkt.Term == 25) {
                                        tmpFixed25.push(pdkt)
                                    } else if (pdkt.Term == 20) {
                                        tmpFixed20.push(pdkt)
                                    } else if (pdkt.Term == 15) {
                                        tmpFixed15.push(pdkt)
                                    } else if (pdkt.Term == 10) {
                                        tmpFixed10.push(pdkt)
                                    }
                                }
                            }
                        })
                    })
                })

                const compareRate = (a, b) => {
                    if (a.base_rate !== b.base_rate)
                        return a.base_rate - b.base_rate
                    return a.closing_cost - b.closing_cost
                }

                const cullRate = (products) => {
                    const filteredPrds = products
                    if (filteredPrds.length === 0) return filteredPrds
                    const newproducts = [filteredPrds[0]]

                    let base_rate = filteredPrds[0].base_rate
                    let closing_cost = filteredPrds[0].closing_cost
                    for (let i = 1; i < filteredPrds.length; i++) {
                        if (filteredPrds[i].base_rate !== base_rate) {
                            if (filteredPrds[i].closing_cost < closing_cost) {
                                newproducts.push(filteredPrds[i])
                                closing_cost = filteredPrds[i].closing_cost
                            }
                            base_rate = filteredPrds[i].base_rate
                        }
                    }

                    return newproducts
                }

                const getIndex = (loans) => {
                    let bestIndex = 0
                    let bestPoints = 1000000
                    for (let i = 0; i < loans.length; i++) {
                        if (
                            Math.abs(loans[i].lender_credit - 1500) < bestPoints
                        ) {
                            bestPoints = Math.abs(loans[i].lender_credit - 1500)
                            bestIndex = i
                        }
                    }

                    return bestIndex
                }

                const prepareRates = (rates) => {
                    const sortedRates = rates.sort(compareRate)
                    return cullRate(sortedRates)
                }

                tmpFixed30 = prepareRates(tmpFixed30)
                tmpFixed25 = prepareRates(tmpFixed25)
                tmpFixed20 = prepareRates(tmpFixed20)
                tmpFixed15 = prepareRates(tmpFixed15)
                tmpFixed10 = prepareRates(tmpFixed10)
                tmpArm56 = prepareRates(tmpArm56)
                tmpArm76 = prepareRates(tmpArm76)
                tmpArm106 = prepareRates(tmpArm106)

                setFixed30(tmpFixed30)
                setFixed25(tmpFixed25)
                setFixed20(tmpFixed20)
                setFixed15(tmpFixed15)
                setFixed10(tmpFixed10)
                setArm56(tmpArm56)
                setArm76(tmpArm76)
                setArm106(tmpArm106)

                setIndexFixed30(getIndex(tmpFixed30))
                setIndexFixed25(getIndex(tmpFixed25))
                setIndexFixed20(getIndex(tmpFixed20))
                setIndexFixed15(getIndex(tmpFixed15))
                setIndexFixed10(getIndex(tmpFixed10))
                setIndexArm56(getIndex(tmpArm56))
                setIndexArm76(getIndex(tmpArm76))
                setIndexArm106(getIndex(tmpArm106))

                setAllRates({
                    fixed30: tmpFixed30,
                    fixed25: tmpFixed25,
                    fixed20: tmpFixed20,
                    fixed15: tmpFixed15,
                    fixed10: tmpFixed10,
                    arm56: tmpArm56,
                    arm76: tmpArm76,
                    arm106: tmpArm106,
                })

                // Choose which term to display. NOTE: the fixed30/arm56 *state*
                // vars lag one render, so select from the freshly-computed tmp
                // buckets. If the active term (default fixed30) has no products
                // for this scenario — e.g. the backend returned no 30-year
                // program — fall back to the first term that does. Otherwise
                // tableData stays null and the widget spins forever.
                const computedBuckets = {
                    fixed30: tmpFixed30,
                    fixed25: tmpFixed25,
                    fixed20: tmpFixed20,
                    fixed15: tmpFixed15,
                    fixed10: tmpFixed10,
                    arm56: tmpArm56,
                    arm76: tmpArm76,
                    arm106: tmpArm106,
                }
                const termFallbackOrder = [
                    currentMtype,
                    'fixed30', 'fixed25', 'fixed20', 'fixed15', 'fixed10',
                    'arm56', 'arm76', 'arm106',
                ]
                const activeTerm = termFallbackOrder.find(
                    (k) => computedBuckets[k] && computedBuckets[k].length !== 0
                )
                if (activeTerm) {
                    setLoans(computedBuckets[activeTerm])
                    if (activeTerm !== currentMtype) setCurrentMtype(activeTerm)
                } else {
                    setLoans([])
                }

                onRatesUpdate({
                    request: body,
                    response: {
                        rates: {
                            fixed30: tmpFixed30,
                            fixed25: tmpFixed25,
                            fixed20: tmpFixed20,
                            fixed15: tmpFixed15,
                            fixed10: tmpFixed10,
                            arm56: tmpArm56,
                            arm76: tmpArm76,
                            arm106: tmpArm106,
                        },
                        currentProduct: currentMtype,
                        selectedRate: allRates[currentMtype]?.[indexFixed30],
                    },
                })
            } catch (err) {
                setOnUpdateRates(false)
            } finally {
                lastCompletedQuoteKeyRef.current = quoteKey
                setLoading(false)
            }
        },
        [
            state,
            county,
            purpose,
            getLoanAmount,
            units,
            getLtv,
            fico,
            occupancy,
            propertyType,
            estOtherCosts,
            monthlyIncome,
            monthlyDebt,
            escrowWaiver,
            selectedLockDay,
            customerid,
            onRatesUpdate,
            pricingIncomeOption,
            hasSelectedCapability,
            primaryIncome,
            loid,
        ]
    )

    const getLoanLimits = useCallback(async () => {
        setLoading(true)
        try {
            const res = await fetch('/data/loanlimits')
            if (!res.ok) {
                console.warn('Fetching loan limits failed:', res)
                throw Error(res.statusText)
            }
            const data = await res.json()
            setLoanLimits(data)
        } catch (err) {
            console.error('Error fetching loan limits:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    const getCustomerUrl = useCallback(async () => {
        try {
            const res = await fetch('/data/customerurls/' + customerid)
            if (!res.ok) {
                console.warn('Fetching customer URL failed:', res)
                throw Error(res.statusText)
            }
            const data = await res.json()
            if (window.location.hostname.includes('localhost')) {
                setCustomerUrl('http://localhost:3000')
            } else {
                setCustomerUrl(data.Prodapp)
            }
        } catch (err) {
            console.error('Error fetching customer URL:', err)
        }
    }, [customerid])

    useEffect(() => {
        const controller = new AbortController()

        const initData = async () => {
            try {
                await Promise.all([
                    getCustomerUrl(),
                    getLoanLimits(),
                ])
            } catch (err) {
                console.error('Error initializing data:', err)
            }
        }

        initData()

        const handler = () => {
            initData()
        }
        eventBus.on('closingFeeUpdated', handler)

        return () => {
            controller.abort()
            eventBus.off('closingFeeUpdated', handler)
        }
    }, [])

    useEffect(() => {
        if (propertyType === 'manufacturedhome') {
            setCurrentMtype('fixed25')
        }
    }, [propertyType])

    useEffect(() => {
        const controller = new AbortController()
        if (onUpdateRates) getProducts(controller)

        return () => {
            controller.abort()
        }
    }, [onUpdateRates])

    useEffect(() => {
        const controller = new AbortController()
        setMessage('')
        setAllRates(createEmptyRates())
        setLoans([])
        setLendersNotSet(false)

        if (hasSelectedCapability) {
            getProducts(controller)
        } else {
            setOnUpdateRates(false)
            setLoading(false)
        }

        return () => controller.abort()
        // State and Primary Income are the two category-routing inputs. Other
        // form changes continue to use the existing Check rates interaction.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, primaryIncome, hasSelectedCapability])

    const stateOptions = useMemo(() => {
        const availableStates = new Set(Object.keys(loanLimits))
        const licensedStateNames = normalizedStateCapabilities
            .map((capability) =>
                com.mapAbbrFullName[capability.state]?.toUpperCase()
            )
            .filter((stateName) => stateName && availableStates.has(stateName))

        return Array.from(new Set(licensedStateNames))
            .sort((a, b) => a.localeCompare(b))
            .map((stateName) => (
                <Select.Option key={stateName} value={stateName}>
                    {com.capitalize(stateName)}
                </Select.Option>
            ))
    }, [loanLimits, normalizedStateCapabilities])

    const countyOptions = useMemo(() => {
        const options = []
        if (state in loanLimits) {
            const keys = Object.keys(loanLimits[state])
            keys.forEach((x) => {
                options.push(
                    <Select.Option key={x} value={x}>
                        {com.capitalize(x.replace('COUNTY', ''))}
                    </Select.Option>
                )
            })
        }
        return options
    }, [loanLimits, state])

    const handleStateChange = useCallback(
        (value) => {
            setState(value)
            setMessage('')
            setAllRates(createEmptyRates())
            setLoans([])

            const firstCounty = Object.keys(loanLimits[value] || {})[0]
            setCounty(firstCounty || '')
        },
        [loanLimits]
    )

    useEffect(() => {
        if (stateOptions.length === 0) return

        const licensedStateNames = stateOptions.map((option) => option.props.value)
        if (licensedStateNames.includes(state)) return

        const firstLicensedState = licensedStateNames[0]
        setState(firstLicensedState)
        const firstCounty = Object.keys(loanLimits[firstLicensedState] || {})[0]
        setCounty(firstCounty || '')
    }, [loanLimits, state, stateOptions])

    const updateProperValue = (e) => {
        const val = e.target.value
        if (val === '') {
            setPropertyValue('')
        } else {
            setPropertyValue(parseInt(val))
        }
    }

    const updateDownPayment = (e) => {
        const val = e.target.value
        if (val === '') {
            setDownPayment('')
            setLtvPercent(0)
        } else {
            const newDownPayment = parseInt(val)
            setDownPayment(newDownPayment)
            // Calculate and update LTV percentage based on the new down payment
            if (purpose === 'Purchase') {
                setLtvPercent(
                   ((newDownPayment / propertyValue) * 100).toFixed(2)
                )
            } else {
                setLtvPercent(
                    ((newDownPayment / propertyValue) * 100).toFixed(2)
                )
            }
        }
    }

    const updateLTV = (e) => {
        const val = e.target.value
        if (val === '') {
            setLtvPercent(0)
        } else {
            const p = parseFloat(val)
            setLtvPercent(p)
            setDownPayment(Math.round(propertyValue * 0.01 * p))
        }
    }

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault()
            event.stopPropagation()

            setIsGMCCJumbo(false)

            if (!hasSelectedCapability) {
                setOnUpdateRates(false)
                return false
            }

            if (formRef.current.reportValidity() === false) {
                setValidated(true)
                return false
            }

            setDownPayment(Math.round(propertyValue * 0.01 * ltvPercent))

            // if (parseInt(downPayment) === 0) {
            //     setValidated(true)
            //     alert("Down payment can't be zero")
            //     return false
            // }

            if (purpose === 'Purchase' && parseInt(downPayment) >= parseInt(propertyValue)) {
                setValidated(true)
                alert("Down payment can't be bigger than purchase price")
                return false
            }

            if (parseInt(fico) < 300 || parseInt(fico) > 1000) {
                setValidated(true)
                alert('FICO score should be in the range of 300 - 1000')
                return false
            }

            let overConforming = false
            if (state in loanLimits && loanLimits[state][county]) {
                let _units = 1
                if (propertyType === 'twotofourfamily') _units = units

                const lldata = loanLimits[state][county]
                let ll
                switch (parseInt(_units)) {
                    case 1:
                        ll = lldata.OneUnit < 832750 ? lldata.OneUnit : 832750
                        break
                    case 2:
                        ll = lldata.TwoUnit < 1066250 ? lldata.TwoUnit : 1066250
                        break
                    case 3:
                        ll =
                            lldata.ThreeUnit < 1288800
                                ? lldata.ThreeUnit
                                : 1288800
                        break
                    case 4:
                        ll =
                            lldata.FourUnit < 1601750
                                ? lldata.FourUnit
                                : 1601750
                        break
                    default:
                        break
                }

                if (getLoanAmount() > ll) {
                    overConforming = true
                }
            }

            if (customerid === 'gmcc' && overConforming) {
                setIsGMCCJumbo(true)
                return
            }

            setOnUpdateRates(true)
            return false
        },
        [
            loanLimits,
            state,
            county,
            fico,
            propertyValue,
            downPayment,
            propertyType,
            ltvPercent,
            pricingIncomeOption,
            hasSelectedCapability,
        ]
    )

    const preQualificationLink = useMemo(() => {
        // googain sends borrowers to its own website rather than to any
        // application, unless the loan officer set a link of their own.
        if (customerid === 'googain') {
            return validatedURL(appLink) || 'https://www.googain.com'
        }
        return getApplicationLink({
            customerid,
            loid,
            appLink,
            appUrl: customerUrl,
            leadsChannel: 'personalsite-apply',
        })
    }, [customerid, loid, appLink, customerUrl])

    const estimatedClosingCost = useMemo(() => {
        if (allRates[currentMtype] && allRates[currentMtype].length > 0) {
            if (selectedPoint === 'All') {
                const max = allRates[currentMtype][0].closing_cost
                const min = Math.max(
                    allRates[currentMtype][allRates[currentMtype].length - 1]
                        .closing_cost,
                    0
                )
                return `$${com.commaize(min)} - $${com.commaize(max)}`
            } else if (selectedPoint === '0') {
                return `$0`
            } else if (selectedPoint === '0-1') {
                const highBoundry = getLoanAmount() * 0.01
                let max = allRates[currentMtype][0].closing_cost
                for (let i = 0; i < allRates[currentMtype].length; i++) {
                    if (allRates[currentMtype][i].closing_cost <= highBoundry) {
                        max = allRates[currentMtype][i].closing_cost
                        break
                    }
                }
                return `$0 - $${com.commaize(max)}`
            } else if (selectedPoint === '1-2') {
                const highBoundry = getLoanAmount() * 0.02
                const lowBoundry = getLoanAmount() * 0.01
                let max = allRates[currentMtype][0].closing_cost
                let min = allRates[currentMtype][0].closing_cost
                for (let i = 0; i < allRates[currentMtype].length; i++) {
                    if (allRates[currentMtype][i].closing_cost <= highBoundry) {
                        max = allRates[currentMtype][i].closing_cost
                        break
                    }
                }
                for (let i = allRates[currentMtype].length - 1; i >= 0; i--) {
                    if (allRates[currentMtype][i].closing_cost >= lowBoundry) {
                        min = allRates[currentMtype][i].closing_cost
                        break
                    }
                }
                return `$${com.commaize(Math.round(min))} - $${com.commaize(Math.round(max))}`
            }
        }

        return '-'
    }, [selectedPoint, allRates, currentMtype])

    const rateChangeDay = useMemo(() => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' }
        let today = new Date()
        const dayOfWeek = today.getDay()
        if (dayOfWeek === 0 || dayOfWeek === 6) {
            // If today is Sunday (0), subtract 2 days to get Friday
            // If today is Saturday (6), subtract 1 day to get Friday
            today = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate() - (dayOfWeek === 0 ? 2 : 1)
            )
        }
        return today.toLocaleDateString('en-US', options)
    }, [])

    const termDivs = useMemo(() => {
        if (isEmpty(allRates)) return <></>

        const divs = []
        for (const [mtype, rates] of Object.entries(allRates)) {
            if (rates.length === 0) continue
            divs.push(
                <div
                    key={mtype}
                    className={`term-item ${
                        mtype === currentMtype ? 'selected' : ''
                    }`}
                    onClick={() => {
                        setCurrentMtype(mtype)
                    }}
                >
                    {TERMS_MAP[mtype]}
                </div>
            )
        }
        return divs
    }, [allRates, currentMtype])

    const tableData = useMemo(() => {
        if (
            !allRates ||
            !currentMtype ||
            !allRates[currentMtype] ||
            allRates[currentMtype].length === 0
        ) {
            return null
        }

        // Filter rates based on selectedPoint
        let filteredIndexes = []

        if (selectedPoint === 'All') {
            // Use existing logic for "All"
            let point1Diff = 999
            let point1I = 0
            let point0Diff = 999
            let point0I = 0

            for (var i = 0; i < allRates[currentMtype].length; i++) {
                const diff0 = Math.abs(allRates[currentMtype][i].points)
                const diff1 = Math.abs(allRates[currentMtype][i].points - 1)
                if (diff0 < point0Diff) {
                    point0I = i
                    point0Diff = diff0
                }
                if (diff1 < point1Diff) {
                    point1I = i
                    point1Diff = diff1
                }
            }

            const indexes = new Set()
            indexes.add(point0I)
            indexes.add(point1I)

            let indexdiff = 2

            let start = point1I + 1
            while (
                start <= allRates[currentMtype].length - 1 &&
                start - point1I <= indexdiff
            ) {
                indexes.add(start)
                start++
            }

            if (point0I + 1 <= allRates[currentMtype].length - 1) {
                indexes.add(point0I + 1)
            }
            if (point0I + 2 <= allRates[currentMtype].length - 1) {
                indexes.add(point0I + 2)
            }
            if (point0I + 3 <= allRates[currentMtype].length - 1) {
                indexes.add(point0I + 3)
            }

            filteredIndexes = Array.from(indexes).sort((a, b) => a - b)
        } else {
            // Filter based on points range
            for (let i = 0; i < allRates[currentMtype].length; i++) {
                const pointsPercent = allRates[currentMtype][i].points

                switch (selectedPoint) {
                    case '0':
                        if (pointsPercent > -1 && pointsPercent < 1) {
                            filteredIndexes.push(i)
                        }
                        break
                    case '0-1':
                        // Show rates with points between 0% and 1%
                        if (pointsPercent >= 0 && pointsPercent <= 1) {
                            filteredIndexes.push(i)
                        }
                        break
                    case '1-2':
                        // Show rates with points between 1% and 2%
                        if (pointsPercent > 1 && pointsPercent <= 2) {
                            filteredIndexes.push(i)
                        }
                        break
                }
            }
        }

        // Format the data for display
        const data = []
        for (const i of filteredIndexes) {
            const d = allRates[currentMtype][i]
            if (typeof d !== 'undefined') {
                if (data.some(item => item.rate === d.base_rate.toFixed(3) + '%')) {
                    continue
                }
                data.push({
                    rate: d.base_rate.toFixed(3) + '%',
                    apr: d.APR.toFixed(3) + '%',
                    monthlyPayment: '$' + com.commaizeFloat(d.monthly_payment),
                    points: '$' + com.commaizeFloat(d.lender_credit),
                    closingCost: '$' + com.commaizeFloat(Math.round(d.closing_cost)),
                })
            }
        }

        return data
    }, [allRates, currentMtype, selectedPoint, customerid])

    const hasAnyRates = useMemo(
        () => Object.values(allRates).some((rates) => rates.length > 0),
        [allRates]
    )

    const ratesDisplayMode = !hasSelectedCapability
        ? 'unsupported-capability'
        : loading
          ? 'loading'
          : message || !hasAnyRates
            ? 'no-match'
            : 'rates'

    useEffect(() => {
        ratesDisplayModeRef.current = ratesDisplayMode
    }, [ratesDisplayMode])

    const handleApplyClick = useCallback(
        (rowData) => {
            if (preQualificationLink) {
                window.open(preQualificationLink, '_blank')
            }
        },
        [preQualificationLink]
    )

    const displayRatesForMtype = useCallback(() => {
        if (loading || tableData === null) {
            return (
                <div className="text-center w100 justify-content-center  align-content-center">
                    <div id="spinner" className="pt-5 mt-4 ml-5 text-center">
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            )
        }

        if (typeof allRates[currentMtype] === 'undefined') return <></>

        const columns = [
            {
                title: 'Rate',
                dataIndex: 'rate',
                key: 'rate',
                align: 'left',
                width: '16%',
            },
            {
                title: 'APR',
                dataIndex: 'apr',
                key: 'apr',
                align: 'left',
                width: '16%',
            },
            {
                title: 'Mo.Pay',
                dataIndex: 'monthlyPayment',
                key: 'monthlyPayment',
                align: 'left',
                width: '16%',
            },
            {
                title: () => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        Pts/Credit
                        <Tooltip
                            title="A positive number means you have to pay lender to get the lower rates. A negative number means lender will give you a credit to help you with the closing costs."
                            placement="top"
                        >
                            <QuestionCircleOutlined
                                className="question-icon"
                                style={{
                                    position: 'relative',
                                    top: '5px',
                                }}
                            />
                        </Tooltip>
                    </div>
                ),
                dataIndex: 'points',
                key: 'points',
                align: 'left',
                width: '16%',
            },
            {
                title: () => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        Closing cost
                        <Tooltip
                            title="The closing cost estimate is a cost associated with finalizing your mortgage, covering services like points, insurance, processing, title searches, and other administrative tasks including commission. This fee is typically paid at the time of closing, which is when you officially take ownership of the property."
                            placement="top"
                        >
                            <QuestionCircleOutlined
                                className="question-icon"
                                style={{
                                    position: 'relative',
                                    top: '5px',
                                }}
                            />
                        </Tooltip>
                    </div>
                ),
                dataIndex: 'closingCost',
                key: 'closingCost',
                align: 'left',
                width: '16%',
            },
            {
                title: '',
                key: 'action',
                align: 'right',
                width: '10%',
                render: (_, record) => (
                    <Button
                        style={{
                            cursor: isPreviewMode ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isPreviewMode}
                        onClick={() => handleApplyClick(record)}
                    >
                        Apply
                    </Button>
                ),
            },
        ]

        if (tableData.length === 0) {
            return (
                <div className="rates-warning">
                    <div className="justify-content-md-center">
                        <img
                            src="/images/warningSign.png"
                            alt="warning"
                            style={{ maxWidth: '20px', marginRight: '10px' }}
                        />
                        <span>
                            There are no eligible products available for this
                            type of amortization.
                        </span>
                    </div>
                </div>
            )
        }

        return (
            <>
                <Table
                    dataSource={tableData}
                    columns={columns}
                    pagination={false}
                    rowKey="rate"
                    className="rates-table"
                />

                {isMobile ? (
                    <>
                        <button
                            className="disclaimer"
                            onClick={() => setShowDisclaimer(!showDisclaimer)}
                        >
                            Disclaimer
                        </button>
                        {showDisclaimer && (
                            <p
                                style={{
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    color: '#6e6e70',
                                }}
                            >
                                The rate quotations and program offerings
                                detailed herein are exclusively applicable to
                                applicants who can provide income verification
                                and have no more than two properties. As of now,
                                quotes are only for conforming loan amount.
                                These offerings are contingent upon the
                                successful completion of our underwriting
                                process and may be revised in response to market
                                dynamics without prior notification.
                                Furthermore, additional limitations may be
                                enforced. It is imperative to understand that
                                the information provided does not constitute a
                                definitive credit decision.
                            </p>
                        )}
                    </>
                ) : (
                    <>
                        <p className="disclaimer">Disclaimer:</p>
                        <p className="disclaimer">
                            The rate quotations and program offerings detailed
                            herein are exclusively applicable to applicants who
                            can provide income verification and have no more
                            than two properties. As of now, quotes are only for
                            conforming loan amount. These offerings are
                            contingent upon the successful completion of our
                            underwriting process and may be revised in response
                            to market dynamics without prior notification.
                            Furthermore, additional limitations may be enforced.
                            It is imperative to understand that the information
                            provided does not constitute a definitive credit
                            decision.
                        </p>
                    </>
                )}
            </>
        )
    }, [allRates, currentMtype, isMobile, loading, showDisclaimer, tableData])

    const handleContactClick = useCallback(() => {
        if (onContact) {
            onContact()
        } else if (preQualificationLink) {
            window.open(preQualificationLink, '_blank')
        }
    }, [onContact, preQualificationLink])

    const renderRatesResults = () => {
        if (ratesDisplayMode === 'loading') {
            return (
                <div className="personal-rates-loading">
                    <Spinner animation="border" variant="primary" />
                </div>
            )
        }

        if (ratesDisplayMode === 'unsupported-capability') {
            const stateName = com.capitalize(state)
            const category = pricingIncomeOption.category
            return (
                <div className="personal-rates-empty personal-rates-capability-empty">
                    <div className="personal-rates-empty-icon">!</div>
                    <h3>
                        {loName || firstName} doesn&apos;t offer this loan type in{' '}
                        {stateName}.
                    </h3>
                    <p>
                        You selected a {category} income type, and {firstName}{' '}
                        isn&apos;t set up for {category} loans in {stateName} yet.
                        Try switching your Primary Income above, or contact{' '}
                        {firstName} for help finding the right fit.
                    </p>
                    <button
                        type="button"
                        className="personal-rates-primary-button"
                        onClick={handleContactClick}
                    >
                        Contact {firstName}
                    </button>
                </div>
            )
        }

        if (lenderNotSet) return null

        if (ratesDisplayMode === 'no-match') {
            return (
                <div className="personal-rates-empty personal-rates-no-match-empty">
                    <div className="personal-rates-empty-icon">!</div>
                    <h3>No matching loan programs for the current criteria.</h3>
                    <p>
                        {message ||
                            `Try adjusting your inputs, or contact ${loName || firstName} for a tailored quote.`}
                    </p>
                    <div className="personal-rates-empty-actions">
                        <button
                            type="button"
                            className="personal-rates-secondary-button"
                            onClick={() =>
                                formRef.current?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                })
                            }
                        >
                            Adjust inputs
                        </button>
                        <button
                            type="button"
                            className="personal-rates-primary-button"
                            onClick={handleContactClick}
                        >
                            Contact {firstName}
                        </button>
                    </div>
                </div>
            )
        }

        return (
            <div style={{ backgroundColor: 'transparent' }}>
                <div className="personal-rates-summary">
                    <div className="rates-date">
                        Rate as of {rateTimestamp} (Changes daily)
                    </div>
                    <span
                        className={`personal-rates-category-badge ${
                            pricingIncomeOption.category === 'QM'
                                ? 'personal-rates-category-badge-qm'
                                : 'personal-rates-category-badge-non-qm'
                        }`}
                    >
                        {pricingIncomeOption.category}
                    </span>
                </div>

                {customerid === 'gmcc' ? (
                    <div className="rates-disclaimer">
                        *Rate quotations currently are estimates for Conforming
                        products only and subject to market changes without
                        advance notice. High Balance and Jumbo products coming
                        soon.
                    </div>
                ) : (
                    <div className="rates-disclaimer">
                        *Rates are updated periodically to reflect real-time
                        lender pricing; some lenders may not update their rates
                        daily.
                    </div>
                )}

                <div className="terms-container">{termDivs}</div>
                {displayRatesForMtype()}
            </div>
        )
    }

    const helocApplyLink = useMemo(() => {
        const { isValid, url } = getValidatedURL(helocLink)
        return isValid ? url : ''
    }, [helocLink])

    const heloanApplyLink = useMemo(() => {
        const { isValid, url } = getValidatedURL(heloanLink)
        return isValid ? url : ''
    }, [heloanLink])

    const handleHelocApplyClick = useCallback(() => {
        if (helocApplyLink) {
            window.open(helocApplyLink, '_blank')
        }
    }, [helocApplyLink])

    const handleHeloanApplyClick = useCallback(() => {
        if (heloanApplyLink) {
            window.open(heloanApplyLink, '_blank')
        }
    }, [heloanApplyLink])

    const helocEntry =
        !showHomeEquity || (!helocApplyLink && !heloanApplyLink) ? null : (
        <div
            style={{
                position: 'relative',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? '14px' : '20px',
                background: 'linear-gradient(180deg, #F2F7FF 0%, #ECF2FE 100%)',
                border: '1px solid #D5E2FB',
                borderRadius: '14px',
                padding: isMobile ? '18px' : '20px 22px',
                marginTop: '28px',
            }}
        >
            <div
                style={{
                    flexShrink: 0,
                    width: '54px',
                    height: '54px',
                    borderRadius: '13px',
                    background: '#1668E3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M3 11.5 12 4l9 7.5" />
                    <path d="M5 10v9h14v-9" />
                    <path d="M10 19v-5h4v5" />
                </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '4px',
                    }}
                >
                    <h3
                        style={{
                            fontFamily: "'Lora', Georgia, serif",
                            fontSize: '21px',
                            fontWeight: 600,
                            color: '#33475B',
                            margin: 0,
                        }}
                    >
                        Tap into your home equity
                    </h3>
                    <span
                        style={{
                            background: '#1668E3',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.6px',
                            padding: '3px 8px',
                            borderRadius: '5px',
                        }}
                    >
                        NEW
                    </span>
                </div>
                <p
                    style={{
                        margin: 0,
                        fontSize: '15px',
                        color: '#5B6470',
                        lineHeight: 1.45,
                    }}
                >
                    Apply for a HELOC with {firstName} and access your home&rsquo;s
                    value &mdash; no need to refinance your current mortgage.
                </p>
            </div>
            <div
                style={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '10px',
                    width: isMobile ? '100%' : 'auto',
                }}
            >
                {helocApplyLink && (
                    <button
                        onClick={handleHelocApplyClick}
                        style={{
                            width: isMobile ? '100%' : 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            border: 'none',
                            background: '#1668E3',
                            color: '#fff',
                            borderRadius: '9px',
                            padding: '13px 22px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Apply for HELOC
                        <svg
                            width="17"
                            height="17"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </button>
                )}
                {heloanApplyLink && (
                    <button
                        onClick={handleHeloanApplyClick}
                        style={{
                            width: isMobile ? '100%' : 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            border: '1.5px solid #1668E3',
                            background: '#fff',
                            color: '#1668E3',
                            borderRadius: '9px',
                            padding: '13px 22px',
                            fontSize: '16px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Apply for HELOAN
                        <svg
                            width="17"
                            height="17"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1668E3"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    )

    if (isMobile) {
        return (
            <div className="mortgage-rates-page">
                {/* Header Section */}
                <div style={{ marginBottom: '11px' }}>
                    <div style={{ color: '#081265', marginBottom: '13px' }}>
                        <span className="main-title">
                            Mortgage rates{' '}
                            <span className="sub-title">with {firstName}</span>
                        </span>
                    </div>
                    {showAffordability && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                            }}
                        >
                            <span className="calculator-hint">
                                Unsure what home price to consider?
                            </span>
                            <Button
                                className="affordability-button"
                                onClick={() => {
                                    if(isPreviewMode) return;
                                    setCalculatorOpen(true)
                                }}
                            >
                                Affordability Calculator
                            </Button>
                        </div>
                    )}
                </div>

                {/* Form Section */}
                <Form
                    ref={formRef}
                    noValidate
                    validated={validated}
                    onSubmit={handleSubmit}
                    className="rates-form"
                >
                    {/* First Row */}
                    <div className="form-row" style={{ minHeight: '70px' }}>
                        <div>
                            <label className="form-label">Loan purpose</label>
                            <Select
                                style={{ width: '100%' }}
                                value={purpose}
                                onChange={(value) => setPurpose(value)}
                                required
                            >
                                <Select.Option value="Purchase">
                                    Purchase
                                </Select.Option>
                                <Select.Option value="RateTermRefi">
                                    Refinance
                                </Select.Option>
                                <Select.Option value="CashOutRefi">
                                    Cash Out Refinance
                                </Select.Option>
                            </Select>
                        </div>
                        <div>
                            <label className="form-label">State</label>
                            <Select
                                style={{ width: '100%' }}
                                value={state}
                                onChange={handleStateChange}
                                required
                            >
                                {stateOptions}
                            </Select>
                        </div>
                    </div>

                    {/* Second Row */}
                    <div className="form-row">
                        {state !== 'TEXAS' && (
                            <div>
                                <label className="form-label">County</label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={county}
                                    onChange={(value) => setCounty(value)}
                                    required
                                >
                                    {countyOptions}
                                </Select>
                            </div>
                        )}
                        <div>
                            <label className="form-label">Credit score</label>
                            <Input
                                style={{ width: '100%' }}
                                value={fico}
                                onChange={(e) => {
                                    const val =
                                        e.target.value === ''
                                            ? ''
                                            : parseInt(e.target.value)
                                    setFico(val)
                                }}
                                required
                            />
                        </div>
                    </div>

                    {/* Third Row */}
                    <div className="form-row">
                        <div>
                            <label className="form-label">
                                {purpose === 'Purchase'
                                    ? 'Est. purchase price'
                                    : 'Home value'}
                            </label>
                            <div className="down-payment-row">
                                <div className="form-slider-outline">
                                    <span className="dollar-sign">$</span>
                                    <CommaizedInput
                                        className="commaized-input"
                                        value={propertyValue}
                                        onChange={updateProperValue}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="form-label">
                                {purpose === 'Purchase'
                                    ? 'Down payment'
                                    : 'Loan amount'}
                            </label>
                            <div className="down-payment-row">
                                <div className="form-slider-outline down-payment-amount">
                                    <span className="dollar-sign">$</span>
                                    <CommaizedInput
                                        className="commaized-input"
                                        value={downPayment}
                                        onChange={updateDownPayment}
                                    />
                                </div>
                                <div className="down-payment-percentage">
                                    <Input
                                        type="number"
                                        className="percentage-input"
                                        suffix="%"
                                        value={ltvPercent}
                                        onChange={(e) => updateLTV(e)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gross monthly income and monthly liabilities */}

                    <div className="form-row">
                        <div>
                            <label className="form-label">
                                Gross monthly income
                            </label>
                            <div className="numerical-input-wrapper">
                                <NumericalInput
                                    size="sm"
                                    value={monthlyIncome}
                                    required
                                    onChange={(e) =>
                                        setMonthlyIncome(e.target.value)
                                    }
                                    min={0}
                                    style={{ width: 'auto' }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">
                                Monthly liabilities
                            </label>
                            <div className="numerical-input-wrapper">
                                <NumericalInput
                                    size="sm"
                                    value={monthlyDebt}
                                    required
                                    onChange={(e) =>
                                        setMonthlyDebt(e.target.value)
                                    }
                                    min={0}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-row primary-income-row">
                        <div>
                            <label className="form-label">Primary Income</label>
                            <Select
                                style={{ width: '100%' }}
                                value={primaryIncome}
                                onChange={setPrimaryIncome}
                                placeholder="Select primary income type"
                            >
                                {PRIMARY_INCOME_OPTIONS.map((option) => (
                                    <Select.Option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {/* Fifth Row */}
                    <div className="form-row" style={{ minHeight: '70px' }}>
                        <div>
                            <label className="form-label">Property type</label>
                            <Select
                                style={{ width: '100%' }}
                                value={propertyType}
                                onChange={(value) => setPropertyType(value)}
                                required
                            >
                                <Select.Option value="singlefamilydetached">
                                    Single Family Home, Detached
                                </Select.Option>
                                <Select.Option value="singlefamilyattached">
                                    Single Family Home, Attached
                                </Select.Option>
                                <Select.Option value="condominiumdetached">
                                    Condominium, Detached
                                </Select.Option>
                                <Select.Option value="condominium">
                                    Condominium, Attached
                                </Select.Option>
                                <Select.Option value="pudattached">
                                    Planned Unit Development, attached
                                </Select.Option>
                                <Select.Option value="puddetached">
                                    Planned Unit Development, detached
                                </Select.Option>
                                <Select.Option value="twotofourfamily">
                                    2 to 4 Unit Multifamily
                                </Select.Option>
                                <Select.Option value="manufacturedhome">
                                    Manufactured Home
                                </Select.Option>
                            </Select>
                        </div>
                        {propertyType === 'twotofourfamily' && (
                            <div>
                                <label className="form-label">
                                    Number of Units
                                </label>
                                <Select
                                    style={{ width: '100%' }}
                                    value={units}
                                    onChange={(value) => setUnits(value)}
                                    required
                                >
                                    <Select.Option value="2">2</Select.Option>
                                    <Select.Option value="3">3</Select.Option>
                                    <Select.Option value="4">4</Select.Option>
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Sixth Row */}
                    <div className="form-row">
                        <div>
                            <label className="form-label">Occupancy</label>
                            <Select
                                style={{ width: '100%' }}
                                value={occupancy}
                                onChange={(value) => setOccupancy(value)}
                            >
                                <Select.Option value="principal">
                                    Primary Residence
                                </Select.Option>
                                <Select.Option value="secondhome">
                                    Secondary Home
                                </Select.Option>
                                <Select.Option value="investment">
                                    Investment
                                </Select.Option>
                            </Select>
                        </div>
                        <div>
                            <label className="form-label">Impound/Escrow</label>
                            <Select
                                style={{ width: '100%' }}
                                value={escrowWaiver}
                                onChange={(value) => setEscrowWaiver(value)}
                                required
                            >
                                <Select.Option value={true}>No</Select.Option>
                                <Select.Option value={false}>Yes</Select.Option>
                            </Select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div>
                            <label className="form-label">Lock Period</label>
                            <Select
                                style={{ width: '100%' }}
                                value={selectedLockDay}
                                onChange={setSelectedLockDay}
                            >
                                <Select.Option value={15}>15 Days</Select.Option>
                                <Select.Option value={30}>30 Days</Select.Option>
                                <Select.Option value={45}>45 Days</Select.Option>
                                <Select.Option value={60}>60 Days</Select.Option>
                                <Select.Option value={75}>75 Days</Select.Option>
                                <Select.Option value={90}>90 Days</Select.Option>
                            </Select>
                        </div>
                    </div>

                    {/* Points and Estimated closing cost */}
                    <div className="form-row">
                        <div>
                            <div className="points-selector">
                                <label className="form-label">Points</label>
                                <Tooltip
                                    title="A positive number means you have to pay lender to get the lower rates. A negative number means lender will give you a credit to help you with the closing costs."
                                    placement="right"
                                >
                                    <QuestionCircleOutlined className="question-icon" />
                                </Tooltip>
                            </div>
                            <PointsSelector
                                selectedPoint={selectedPoint}
                                setSelectedPoint={setSelectedPoint}
                            />
                        </div>
                    </div>

                    {/* Check rates button */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            marginTop: '16px',
                        }}
                    >
                        <Button
                            type="submit"
                            className="check-rates-button"
                            style={{
                                cursor: isPreviewMode
                                    ? 'not-allowed'
                                    : 'pointer',
                            }}
                            disabled={isPreviewMode}
                        >
                            Check rates
                        </Button>
                    </div>
                </Form>

                {/* Rates Section */}
                {lenderNotSet && (
                    <div className="rates-warning">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px',
                            }}
                        >
                            <div style={{ flexShrink: 0, marginTop: '2px' }}>
                                <img
                                    src="/images/warningSign.png"
                                    alt="warning"
                                    style={{ objectFit: 'contain' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                {!isPreviewMode ? (
                                    'This functionality is not ready yet. Please check later.'
                                ) : (
                                    <div>
                                        <p
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                                marginBottom: '4px',
                                            }}
                                        >
                                            No lender has been configured yet.
                                        </p>
                                        <p
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '20px',
                                            }}
                                        >
                                            {' '}
                                            If you are company owner, please go
                                            to "Management" - "Lenders" to
                                            config your lenders.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {renderRatesResults()}

                {helocEntry}

                <AffordabilityModal
                    showQuickApplicationForm={showQuickApplicationForm}
                    open={calculatorOpen}
                    onClose={() => setCalculatorOpen(false)}
                    isFromLanding={true}
                    customerid={customerid}
                    loid={loid}
                    appLink={appLink}
                    companyLink={companyLink}
                    isMobile={true}
                />
            </div>
        )
    }

    return (
        <div className="mortgage-rates-page">
            {/* Header Section */}
            <div className="header-container">
                <div style={{ color: '#081265' }}>
                    <span className="main-title">
                        Mortgage rates{' '}
                        <span className="sub-title">with {firstName}</span>
                    </span>
                </div>
                {showAffordability && (
                    <div className="calculator-container">
                        <span className="calculator-hint">
                            Unsure what home price to consider?
                        </span>
                        <Button
                            className="affordability-button"
                            onClick={() => {
                                if(isPreviewMode) return;
                                setCalculatorOpen(true)
                            }}
                        >
                            Affordability Calculator
                        </Button>
                    </div>
                )}
            </div>

            {/* Form Section */}
            <Form
                ref={formRef}
                noValidate
                validated={validated}
                onSubmit={handleSubmit}
                className="rates-form"
            >
                {/* First Row */}
                <div className="form-row">
                    <div>
                        <label className="form-label">Loan purpose</label>
                        <Select
                            style={{ width: '100%' }}
                            value={purpose}
                            onChange={(value) => setPurpose(value)}
                            required
                        >
                            <Select.Option value="Purchase">
                                Purchase
                            </Select.Option>
                            <Select.Option value="RateTermRefi">
                                Refinance
                            </Select.Option>
                            <Select.Option value="CashOutRefi">
                                Cash Out Refinance
                            </Select.Option>
                        </Select>
                    </div>
                    <div>
                        <label className="form-label">State</label>
                        <Select
                            style={{ width: '100%' }}
                            value={state}
                            onChange={handleStateChange}
                            required
                        >
                            {stateOptions}
                        </Select>
                    </div>
                    {state !== 'TEXAS' && (
                        <div>
                            <label className="form-label">County</label>
                            <Select
                                style={{ width: '100%' }}
                                value={county}
                                onChange={(value) => setCounty(value)}
                                required
                            >
                                {countyOptions}
                            </Select>
                        </div>
                    )}
                    <div>
                        <label className="form-label">Credit score</label>
                        <Input
                            style={{ width: '100%' }}
                            value={fico}
                            onChange={(e) => {
                                const val =
                                    e.target.value === ''
                                        ? ''
                                        : parseInt(e.target.value)
                                setFico(val)
                            }}
                            required
                        />
                    </div>
                </div>

                {/* Second Row */}
                <div className="form-row">
                    <div>
                        <label className="form-label">
                            {purpose === 'Purchase'
                                ? 'Est. purchase price'
                                : 'Home value'}
                        </label>
                        <div className="down-payment-row">
                            <div className="form-slider-outline">
                                <span className="dollar-sign">$</span>
                                <CommaizedInput
                                    className="commaized-input"
                                    value={propertyValue}
                                    onChange={updateProperValue}
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="form-label">
                            {purpose === 'Purchase'
                                ? 'Down payment'
                                : 'Loan amount'}
                        </label>
                        <div className="down-payment-row">
                            <div className="form-slider-outline down-payment-amount">
                                <span className="dollar-sign">$</span>
                                <CommaizedInput
                                    className="commaized-input"
                                    value={downPayment}
                                    onChange={updateDownPayment}
                                />
                            </div>
                            <div className="down-payment-percentage">
                                <Input
                                    type="number"
                                    className="percentage-input"
                                    suffix="%"
                                    value={ltvPercent}
                                    onChange={(e) => updateLTV(e)}
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="form-label">
                            Gross monthly income
                        </label>
                        <div className="numerical-input-wrapper">
                            <NumericalInput
                                size="sm"
                                value={monthlyIncome}
                                required
                                onChange={(e) =>
                                    setMonthlyIncome(e.target.value)
                                }
                                min={0}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="form-label">
                            Primary Income
                        </label>
                        <Select
                            style={{ width: '100%' }}
                            value={primaryIncome}
                            onChange={setPrimaryIncome}
                            placeholder="Select primary income type"
                        >
                            {PRIMARY_INCOME_OPTIONS.map((option) => (
                                <Select.Option
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>

                {/* Third Row */}
                <div className="form-row loan-details-row">
                    <div>
                        <label className="form-label">
                            Monthly liabilities
                        </label>
                        <div className="numerical-input-wrapper">
                            <NumericalInput
                                size="sm"
                                value={monthlyDebt}
                                required
                                onChange={(e) => setMonthlyDebt(e.target.value)}
                                min={0}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="form-label">Property type</label>
                        <Select
                            style={{ width: '100%' }}
                            value={propertyType}
                            onChange={(value) => setPropertyType(value)}
                            required
                        >
                            <Select.Option value="singlefamilydetached">
                                Single Family Home, Detached
                            </Select.Option>
                            <Select.Option value="singlefamilyattached">
                                Single Family Home, Attached
                            </Select.Option>
                            <Select.Option value="condominiumdetached">
                                Condominium, Detached
                            </Select.Option>
                            <Select.Option value="condominium">
                                Condominium, Attached
                            </Select.Option>
                            <Select.Option value="pudattached">
                                Planned Unit Development, attached
                            </Select.Option>
                            <Select.Option value="puddetached">
                                Planned Unit Development, detached
                            </Select.Option>
                            <Select.Option value="twotofourfamily">
                                2 to 4 Unit Multifamily
                            </Select.Option>
                            <Select.Option value="manufacturedhome">
                                Manufactured Home
                            </Select.Option>
                        </Select>
                    </div>
                    <div>
                        <label className="form-label">Occupancy</label>
                        <Select
                            style={{ width: '100%' }}
                            value={occupancy}
                            onChange={(value) => setOccupancy(value)}
                        >
                            <Select.Option value="principal">
                                Primary Residence
                            </Select.Option>
                            <Select.Option value="secondhome">
                                Secondary Home
                            </Select.Option>
                            <Select.Option value="investment">
                                Investment
                            </Select.Option>
                        </Select>
                    </div>
                    <div>
                        <label className="form-label">Impound/Escrow</label>
                        <Select
                            style={{ width: '100%' }}
                            value={escrowWaiver}
                            onChange={(value) => setEscrowWaiver(value)}
                            required
                        >
                            <Select.Option value={true}>No</Select.Option>
                            <Select.Option value={false}>Yes</Select.Option>
                        </Select>
                    </div>
                </div>

                {propertyType === 'twotofourfamily' && (
                    <div className="form-row units-row">
                        <div>
                            <label className="form-label">Number of Units</label>
                            <Select
                                style={{ width: '100%' }}
                                value={units}
                                onChange={(value) => setUnits(value)}
                                required
                            >
                                <Select.Option value="2">2</Select.Option>
                                <Select.Option value="3">3</Select.Option>
                                <Select.Option value="4">4</Select.Option>
                            </Select>
                        </div>
                    </div>
                )}

                {/* Fourth Row */}
                <div className="form-row rates-actions-row">
                    <div className="rates-action-field lock-period-field">
                        <label className="form-label">Lock Period</label>
                        <Select
                            style={{ width: '100%' }}
                            value={selectedLockDay}
                            onChange={setSelectedLockDay}
                        >
                            <Select.Option value={15}>15 Days</Select.Option>
                            <Select.Option value={30}>30 Days</Select.Option>
                            <Select.Option value={45}>45 Days</Select.Option>
                            <Select.Option value={60}>60 Days</Select.Option>
                            <Select.Option value={75}>75 Days</Select.Option>
                            <Select.Option value={90}>90 Days</Select.Option>
                        </Select>
                    </div>
                    <div className="rates-action-field points-field">
                        <div className="points-selector">
                            <label className="form-label">Points</label>
                            <Tooltip
                                title="A positive number means you have to pay lender to get the lower rates. A negative number means lender will give you a credit to help you with the closing costs."
                                placement="right"
                            >
                                <QuestionCircleOutlined className="question-icon" />
                            </Tooltip>
                        </div>
                        <PointsSelector
                            selectedPoint={selectedPoint}
                            setSelectedPoint={setSelectedPoint}
                        />
                    </div>
                    <div className="closing-cost-container">
                        <span className="closing-cost-label">
                            Estimated closing cost:
                        </span>
                        <span className="closing-cost-value">
                            {estimatedClosingCost}
                        </span>
                    </div>
                    <div className="check-rates-container">
                        <Button
                            type="submit"
                            className="check-rates-button"
                            style={{
                                cursor: isPreviewMode
                                    ? 'not-allowed'
                                    : 'pointer',
                            }}
                            disabled={isPreviewMode}
                        >
                            Check rates
                        </Button>
                    </div>
                </div>
            </Form>

            {/* Rates Section */}
            {lenderNotSet && (
                <div className="rates-warning">
                    <Row className="justify-content-md-center">
                        <Col xs={1}>
                            <img src="/images/warningSign.png" alt="warning" />
                        </Col>
                        <Col xs={11}>
                            {!isPreviewMode ? (
                                'This functionality is not ready yet. Please check later.'
                            ) : (
                                <div>
                                    <p>No lender has been configured yet.</p>
                                    <p>
                                        {' '}
                                        If you are company owner, please go to
                                        "Management" - "Lenders" to config your
                                        lenders.
                                    </p>
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            )}
            {renderRatesResults()}

            {helocEntry}

            <AffordabilityModal
                showQuickApplicationForm={showQuickApplicationForm}
                open={calculatorOpen}
                onClose={() => setCalculatorOpen(false)}
                isFromLanding={true}
                customerid={customerid}
                loid={loid}
                appLink={appLink}
                companyLink={companyLink}
            />
        </div>
    )
}

export default MortgageRates
